import React, { useEffect, useRef, useState } from 'react';
import { ISelectItem, PortalDirection } from 'models/commonModel';
import InlineSpinner from 'components/ui/spinner/InlineSpinner';
import FormLabel from '../label/FormLabel';
import styles from './styles.module.scss'

interface Props {
  label?: string;
  labelDesc?: string;
  defaultOptionLabel?: string;
  defaultOptionValue?: string | number;
  value?: string | number;
  inputProps?: any;
  itemsList?: ISelectItem<string | number>[];
  isLoading?: boolean;
  disabled?: boolean;
  isError?: boolean;
  errorMessage?: string;
  customClassName?: string;
  customSelectClassName?: string;
  size?: 'sm' | 'lg';
  onChange?: (value: any) => void;
  dir?: PortalDirection,
}

const CustomSelectField = ({
  label,
  labelDesc,
  value,
  defaultOptionLabel,
  defaultOptionValue,
  inputProps,
  itemsList,
  isLoading,
  disabled,
  isError,
  errorMessage,
  customClassName,
  customSelectClassName,
  size,
  dir,
  onChange
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentValue = value ?? defaultOptionValue ?? '';
  const selectedOption = itemsList?.find(
    x => String(x.value) === String(currentValue)
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (val: string | number) => {
    inputProps?.onChange?.({
      target: { name: inputProps?.name, value: val }
    });

    onChange?.(val);
    setIsOpen(false);
  };

  return (
    <div className={`form-group flex-fill ${customClassName}`}>
      {label && (
        <FormLabel label={label} labelDesc={labelDesc} />
      )}

      <select {...inputProps} value={currentValue} style={{ display: 'none' }} />

      <div ref={containerRef} className="position-relative">
        {isLoading && <InlineSpinner small varient="secondary" />}

        <button
          type="button"
          disabled={disabled}
          dir={dir}
          className={`form-select rounded-12 d-flex align-items-center gap-2
            ${size === 'sm' ? '' : 'p-3'}
            ${isError ? 'is-invalid' : ''}`}
          onClick={() => setIsOpen(prev => !prev)}
        >
          {selectedOption ? (
            <>
              {selectedOption.icon}
              <span>{selectedOption.label}</span>
            </>
          ) : (
            <span className="text-muted">{defaultOptionLabel}</span>
          )}
        </button>

        {isOpen && (
          <ul dir={dir} className="position-absolute w-100 bg-white border shadow-sm mt-1 p-0" style={{
            "zIndex": 9999,
            "top": '100%',
            "left": 0
          }}>
            {itemsList?.map(item => (
              <li
                dir={dir}
                key={item.value}
                className={`px-3 py-2 d-flex align-items-center gap-2 ${styles.optionItem}`}
                onClick={() => handleSelect(item.value)}
                style={{ cursor: 'pointer' }}
              >
                {item.icon}
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        )}

        {errorMessage && (
          <div className="invalid-feedback d-block">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomSelectField;